import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import Layout from "../components/layout/Layout";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import { useBackNavigation } from "../hooks/useBackNavigation";
import { getPropertyById, getUnitDetails } from "../api/unitApi";
import { trimSignatureImageDataUrl } from "../utils/signaturePreview";
import {
  createFME1,
  deleteFME1,
  downloadEditableFme1Pdf,
  getFME1ById,
  getFME1CheckboxSchema,
  getFME1FormFieldSchema,
  getInitialFME1Data,
  saveFME1,
  updateFME1Meta,
} from "../api/fme1Api";
import { getSignatures } from "../api/signaturesApi";
import { getApiErrorMessage } from "../utils/concurrency";

const toDateInput = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const getToday = () => toDateInput(new Date());

const addDays = (dateString, days) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + days);
  return toDateInput(date);
};

const SIGNATURE_CAPACITY_OPTIONS = [
  { value: "LL", label: "Rentcharge Owner" },
  { value: "MC", label: "Management Company" },
  { value: "MA", label: "Managing Agent" },
  { value: "RA", label: "Residents' Association" },
];

const FME1_SECTIONS = [
  {
    id: "section-1",
    tabLabel: "Section 1",
    title: "Section 1 - Property & Contact Details",
    description: "",
    textRange: [0, 53],
    checkboxRange: [0, 14],
  },
  {
    id: "section-2",
    tabLabel: "Section 2",
    title: "Section 2 - Transfer & Registration",
    description: "",
    textRange: [54, 58],
    checkboxRange: [15, 18],
  },
  {
    id: "section-3",
    tabLabel: "Section 3",
    title: "Section 3 - Service Charge",
    description: "",
    textRange: [59, 87],
    checkboxRange: [19, 32],
  },
  {
    id: "section-4",
    tabLabel: "Section 4",
    title: "Section 4 - Insurance",
    description: "",
    textRange: [88, 99],
    checkboxRange: [33, 39],
  },
  {
    id: "section-5",
    tabLabel: "Section 5",
    title: "Section 5 - Disputes",
    description: "",
    textRange: [100, 101],
    checkboxRange: [40, 41],
  },
  {
    id: "section-6",
    tabLabel: "Section 6",
    title: "Section 6 - Required Documents",
    description: "",
    textRange: [],
    checkboxRange: [42, 56],
  },
  {
    id: "signature",
    tabLabel: "Signature",
    title: "Signature",
    description: "",
    textRange: [102, 105],
    checkboxRange: [57, 57],
  },
];

const TOTAL_FME1_SECTIONS = FME1_SECTIONS.length;

const FME1_COMPOSITE_CHECKBOX_GROUPS = [
  {
    keys: ["2.3 membership_required_yes", "2.3 membership_required_no"],
    question: "2.3 Is the incoming Owner required to take a share in, or become a member of, the Management Company?",
    options: [
      { label: "Yes", key: "2.3 membership_required_yes" },
      { label: "No", key: "2.3 membership_required_no" },
    ],
  },
  {
    keys: ["3.7.2 reserve_fund_sufficient_yes", "3.7.2 reserve_fund_sufficient_no"],
    question: "3.7.2 Is the amount expected to be sufficient to cover known expenditure?",
    options: [
      { label: "Yes", key: "3.7.2 reserve_fund_sufficient_yes" },
      { label: "No", key: "3.7.2 reserve_fund_sufficient_no" },
    ],
  },
];

const FME1_COMPOSITE_CHECKBOX_BY_KEY = FME1_COMPOSITE_CHECKBOX_GROUPS.reduce((map, group) => {
  group.keys.forEach((key) => {
    map[key] = group;
  });
  return map;
}, {});

const FME1_NOTICE_PARTIES = [
  { label: "Rentcharge Owner", selectedKey: "1.5 rentcharge_owner_accepts_notice", emailKey: "1.5 rentcharge_owner_accepts_notice_email", amountKey: "1.6 RO £" },
  { label: "Management Company", selectedKey: "1.5 management_company_accepts_notice", emailKey: "1.5 management_company_accepts_notice_email", amountKey: "1.6 MC £" },
  { label: "Managing Agent", selectedKey: "1.5 managing_agent_accepts_notice", emailKey: "1.5 managing_agent_accepts_notice_email", amountKey: "1.6 MA £" },
  { label: "Legal Representative", selectedKey: "1.5 legal_representative_accepts_notice", emailKey: "1.5 legal_representative_accepts_notice_email", amountKey: "1.6 LR £" },
  { label: "Other", selectedKey: "1.5 other_accepts_notice", emailKey: "1.5 other_accepts_notice_email", amountKey: "1.6 Other £" },
];

const contactFieldLabels = (prefix, roleName, { redress = false } = {}) => ({
  [`${prefix} Name`]: `${prefix} ${roleName} Name`,
  [`${prefix} Address`]: `${prefix} ${roleName} Address`,
  [`${prefix} Telephone`]: `${prefix} ${roleName} Telephone`,
  [`${prefix} Email`]: `${prefix} ${roleName} Email`,
  ...(redress ? { [`${prefix} Redress`]: `${prefix} ${roleName} Redress Scheme` } : {}),
  [`${prefix} SC`]: `${prefix} ${roleName} Sort Code`,
  [`${prefix} Account Name`]: `${prefix} ${roleName} Account Name`,
  [`${prefix} Account number`]: `${prefix} ${roleName} Account number`,
  [`${prefix} Reference`]: `${prefix} ${roleName} Reference`,
  [`${prefix} Fee Type`]: `${prefix} ${roleName} Fee Type`,
});

const FME1_SECTION_GROUPS = {
  "section-1": [
    {
      title: "",
      fieldKeys: ["Property", "Post CodeSeller", "Seller", "Post CodeUPRN if known"],
    },
    {
      title: "1.1 Rentcharge Owner",
      labelPrefix: "1.1",
      fieldKeys: [
        "1.1 Name",
        "1.1 Address",
        "1.1 Telephone",
        "1.1 Email",
        "1.1 Redress",
        "1.1 SC",
        "1.1 Account Name",
        "1.1 Account number",
        "1.1 Reference",
        "1.1 Fee Type",
      ],
      fieldLabelOverrides: contactFieldLabels("1.1", "Rentcharge Owner", { redress: true }),
    },
    {
      title: "1.2 Management Company",
      labelPrefix: "1.2",
      fieldKeys: [
        "1.2 Name",
        "1.2 Address",
        "1.2 Telephone",
        "1.2 Email",
        "1.2 Redress",
        "1.2 SC",
        "1.2 Account Name",
        "1.2 Account number",
        "1.2 Reference",
        "1.2 Fee Type",
      ],
      fieldLabelOverrides: contactFieldLabels("1.2", "Management Company", { redress: true }),
    },
    {
      title: "1.3 Managing Agent",
      labelPrefix: "1.3",
      fieldKeys: [
        "1.3 Name",
        "1.3 Address",
        "1.3 Telephone",
        "1.3 Email",
        "1.3 Redress",
        "1.3 SC",
        "1.3 Account Name",
        "1.3 Account number",
        "1.3 Reference",
        "1.3 Fee Type",
      ],
      fieldLabelOverrides: contactFieldLabels("1.3", "Managing Agent", { redress: true }),
    },
    {
      title: "1.4 Legal Representative of one of the above",
      labelPrefix: "1.4",
      fieldKeys: [
        "1.4 Name",
        "1.4 Address",
        "1.4 Telephone",
        "1.4 Email",
        "1.4 SC",
        "1.4 Account Name",
        "1.4 Account number",
        "1.4 Reference",
        "1.4 Fee Type",
      ],
      fieldLabelOverrides: contactFieldLabels("1.4", "Legal Representative"),
      checkboxOrders: [0, 0],
    },
    {
      title: "1.5 Notice of Transfer & Charge",
      noticeLayout: true,
      fieldKeys: [
        "1.6 RO £",
        "1.6 MC £",
        "1.6 MA £",
        "1.6 LR £",
        "1.6 Other £",
        "1.6 Name",
        "1.6 Address",
        "1.6 Telephone",
        "1.6 Email",
        "1.6 Capacity eg Landlords lawyer",
        "1.5.1",
      ],
      checkboxOrders: [1, 11],
    },
    {
      title: "",
      checkboxOrders: [12, 14],
    },
  ],
  "section-2": [
    {
      title: "",
      fieldKeys: ["2.1.1 £", "2.1.2"],
      checkboxOrders: [15, 15],
    },
    {
      title: "",
      fieldKeys: ["2.2.1"],
      checkboxOrders: [16, 16],
    },
    {
      title: "",
      fieldKeys: ["2.3.1"],
      checkboxOrders: [17, 18],
    },
    {
      title: "",
      fieldKeys: ["2.4"],
      question:
        "2.4 What is the procedure and cost for obtaining a certificate in accordance with a restriction in the Proprietorship Register at the Land Registry, if applicable?",
      fieldLabelOverrides: {
        "2.4": "Details",
      },
    },
  ],
  "section-3": [
    { title: "", fieldKeys: ["3.1 £", "3.1"], annualServiceChargeLayout: true },
    { title: "", fieldKeys: ["3.2.1"], checkboxOrders: [19, 19] },
    {
      title: "",
      fieldKeys: [
        "3.3 - From DD",
        "3.3 - From MM",
        "3.3 - From YYYY",
        "3.3 - To DD",
        "3.3 - To MM",
        "3.3 - To YYYY",
      ],
      periodFields: {
        question: "3.3 What period is covered by the last demand?",
        from: ["3.3 - From DD", "3.3 - From MM", "3.3 - From YYYY"],
        to: ["3.3 - To DD", "3.3 - To MM", "3.3 - To YYYY"],
      },
    },
    {
      title: "",
      fieldKeys: ["3.1 RD", "3.1 RL", "3.1 COM"],
      question:
        "3.4 How many properties contribute toward the maintenance of the Managed Area? (Stipulate the number of each applicable type of property)",
      fieldRows: [
        { key: "3.1 RD", label: "Residential Freehold" },
        { key: "3.1 RL", label: "Residential Leasehold" },
        { key: "3.1 COM", label: "Commercial" },
      ],
    },
    {
      title: "",
      fieldKeys: ["3.4.1.1"],
      checkboxOrders: [20, 20],
    },
    { title: "", fieldKeys: ["3.5.1"], checkboxOrders: [21, 21] },
    { title: "", fieldKeys: ["3.6.1"], checkboxOrders: [22, 22] },
    {
      title: "",
      fieldKeys: ["3.7.1 £", "3.7.1 Managed Areas £", "3.7.3"],
      checkboxOrders: [23, 25],
      reserveFundLayout: true,
    },
    {
      title: "",
      fieldKeys: ["3.8 ID DD", "3.8 ID MM", "3.8 ID YYYY", "3.8 ED DD", "3.8 ED MM", "3.8 ED YYYY"],
      checkboxOrders: [26, 27],
      dateRows: {
        question:
          "3.8 If parts of the Managed Areas require regular decoration confirm the date when the Managed Areas were last decorated, internally and externally.",
        rows: [
          {
            label: "Internally:",
            fields: ["3.8 ID DD", "3.8 ID MM", "3.8 ID YYYY"],
            naKey: "3.8 internal_na",
          },
          {
            label: "Externally:",
            fields: ["3.8 ED DD", "3.8 ED MM", "3.8 ED YYYY"],
            naKey: "3.8 external_na",
          },
        ],
      },
    },
    { title: "", fieldKeys: ["3.9.1"], checkboxOrders: [28, 28] },
    { title: "", fieldKeys: ["3.10.1"], checkboxOrders: [29, 29] },
    { title: "", fieldKeys: ["3.11.1"], checkboxOrders: [30, 30] },
    { title: "", fieldKeys: ["3.12.1"], checkboxOrders: [31, 31] },
    { title: "", fieldKeys: ["3.13.1"], checkboxOrders: [32, 32] },
  ],
  "section-4": [
    { title: "", fieldKeys: ["4.1.1.1"], checkboxOrders: [33, 34] },
    {
      title: "",
      fieldKeys: ["4.2 From DD", "4.2 From MM", "4.2 From YYYY", "4.2 To DD", "4.2 To MM", "4.2 To YYYY"],
      periodFields: {
        question: "4.2 What period is covered by the last demand?",
        from: ["4.2 From DD", "4.2 From MM", "4.2 From YYYY"],
        to: ["4.2 To DD", "4.2 To MM", "4.2 To YYYY"],
      },
    },
    { title: "", fieldKeys: ["4.3.1"], checkboxOrders: [35, 35] },
    { title: "", fieldKeys: ["4.4.1"], checkboxOrders: [36, 36] },
    { title: "", checkboxOrders: [37, 37] },
    { title: "", fieldKeys: ["4.6"] },
    { title: "", fieldKeys: ["4.7.1"], checkboxOrders: [38, 38] },
    { title: "", fieldKeys: ["4.8.1"], checkboxOrders: [39, 39] },
  ],
  "section-5": [
    { title: "", fieldKeys: ["5.1.1"], checkboxOrders: [40, 40] },
    { title: "", fieldKeys: ["5.2.1"], checkboxOrders: [41, 41] },
  ],
  "section-6": [
    { title: "", checkboxOrders: [42, 56] },
  ],
  signature: [
    { title: "", fieldKeys: ["Signed", "Dated", "Print Name", "Company"], checkboxOrders: [57, 57], signatureLayout: true },
  ],
};

const cleanFieldStem = (fieldKey = "") =>
  fieldKey
    .replace(/^\d+\s+/, "")
    .replace(/^\d+\.\d+\s+/, "")
    .trim();

const getConfiguredFieldLabel = (field, group) => {
  if (!field) return "";
  if (group?.fieldLabelOverrides?.[field.field_key]) {
    return group.fieldLabelOverrides[field.field_key];
  }
  const defaultLabel = getFieldLabel(field);
  if (!group?.labelPrefix) {
    return defaultLabel;
  }
  const stem = cleanFieldStem(field.field_key) || defaultLabel;
  return `${group.labelPrefix} ${stem}`;
};

const getGroupFields = (group, fields) => {
  if (group.fieldKeys?.length) {
    return group.fieldKeys
      .map((fieldKey) => fields.find((field) => field.field_key === fieldKey))
      .filter(Boolean);
  }
  if (group.fieldOrders?.length) {
    return fields.filter((field) => isInRange(field.order, group.fieldOrders));
  }
  return [];
};

const getGroupCheckboxes = (group, checkboxes) => {
  if (group.checkboxOrders?.length) {
    return checkboxes.filter((field) => isInRange(field.order, group.checkboxOrders));
  }
  return [];
};

const getCheckboxQuestion = (field) => field?.question || field?.key || "";

const getCheckboxOptions = (field) => field?.options || [];

const isCheckedValue = (value) => value === "Checked";

const getFieldLabel = (field) => {
  if (!field) return "";
  return field.question || field.field_key;
};

const isInRange = (value, range) => {
  if (!range?.length) return false;
  return value >= range[0] && value <= range[1];
};

function FME1MetaPanel({ meta, onChange }) {
  const status = meta?.status ?? "";
  const requestedDate = meta?.requestedDate ?? "";
  const dueByDate = meta?.dueByDate ?? "";
  const requestedDateLabel = requestedDate
    ? new Date(requestedDate).toLocaleDateString("en-GB")
    : "";

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          FME1 Status
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="fme1-status-label">Status</InputLabel>
            <Select
              labelId="fme1-status-label"
              label="Status"
              value={status}
              onChange={(event) => onChange({ status: event.target.value })}
            >
              <MenuItem value="Requested">Requested</MenuItem>
              <MenuItem value="In-progress">In-progress</MenuItem>
              <MenuItem value="Complete">Complete</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Requested date"
            value={requestedDateLabel}
            InputProps={{ readOnly: true }}
            size="small"
            fullWidth
          />

          <TextField
            label="Due by date"
            type="date"
            value={dueByDate}
            onChange={(event) => onChange({ dueByDate: event.target.value })}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function FME1Questionnaire({ data, setField }) {
  const questionnaireLocation = useLocation();
  const questionnaireNavigate = useNavigate();
  const [textSchema, setTextSchema] = useState([]);
  const [checkboxSchema, setCheckboxSchema] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const hashSection = questionnaireLocation.hash?.replace("#", "");
  const initialSection = FME1_SECTIONS.some((section) => section.id === hashSection)
    ? hashSection
    : FME1_SECTIONS[0].id;
  const [activeSection, setActiveSection] = useState(initialSection);

  useEffect(() => {
    let active = true;
    const loadSchema = async () => {
      try {
        const [fields, checkboxes, availableSignatures] = await Promise.all([
          getFME1FormFieldSchema(),
          getFME1CheckboxSchema(),
          getSignatures(),
        ]);
        if (!active) return;
        setTextSchema(fields || []);
        setCheckboxSchema(checkboxes || []);
        setSignatures((availableSignatures || []).filter((signature) => signature.is_active));
      } catch (error) {
        console.error("Failed to load FME1 schema", error);
      }
    };
    loadSchema();
    return () => {
      active = false;
    };
  }, []);

  const value = (key) => data?.[key] ?? "";
  const set = (key, nextValue) => setField(key, nextValue);
  const datedValue = data?.Dated ?? "";
  const signatureCapacityValue = data?.signature_capacity ?? "";
  const companyValue = data?.Company ?? "";
  const managingAgentName = data?.["1.3 Name"] ?? "";

  useEffect(() => {
    if (!datedValue) {
      setField("Dated", getToday());
    }
  }, [datedValue, setField]);

  useEffect(() => {
    if (!signatureCapacityValue) {
      setField("signature_capacity", "MA");
    }
  }, [setField, signatureCapacityValue]);

  useEffect(() => {
    if (!companyValue && managingAgentName) {
      setField("Company", managingAgentName);
    }
  }, [companyValue, managingAgentName, setField]);

  const selectedSignatureId = String(value("signature_id") || "");
  const selectedSignature = useMemo(
    () => signatures.find((signature) => String(signature.id) === selectedSignatureId) || null,
    [signatures, selectedSignatureId]
  );
  const selectedSignatureImage =
    selectedSignature?.signature_image_data_url || value("signature_image_data_url");
  const [signaturePreviewImage, setSignaturePreviewImage] = useState("");

  useEffect(() => {
    let active = true;

    const loadPreview = async () => {
      const trimmedImage = await trimSignatureImageDataUrl(selectedSignatureImage);
      if (active) {
        setSignaturePreviewImage(trimmedImage || selectedSignatureImage || "");
      }
    };

    loadPreview();

    return () => {
      active = false;
    };
  }, [selectedSignatureImage]);

  const handleSignatureSelection = (signatureId) => {
    const signature = signatures.find((item) => String(item.id) === String(signatureId)) || null;
    set("signature_id", signature ? String(signature.id) : "");
    set("Signed", signature?.name || "");
    set("Print Name", signature?.name || "");
    set("signature_title", signature?.title || "");
    if (!value("Company")) {
      set("Company", value("1.3 Name") || "");
    }
  };

  const sections = useMemo(
    () =>
      FME1_SECTIONS.filter((section) => {
        const hasText = textSchema.some((field) => isInRange(field.order, section.textRange));
        const hasCheckbox = checkboxSchema.some((field) => isInRange(field.order, section.checkboxRange));
        return hasText || hasCheckbox;
      }),
    [checkboxSchema, textSchema]
  );

  useEffect(() => {
    if (!sections.length) return;
    if (!sections.some((section) => section.id === activeSection)) {
      setActiveSection(sections[0].id);
    }
  }, [activeSection, sections]);

  useEffect(() => {
    if (hashSection && sections.some((section) => section.id === hashSection)) {
      setActiveSection(hashSection);
    }
  }, [hashSection, sections]);

  const currentSection = sections.find((section) => section.id === activeSection) || sections[0];
  const currentSectionIndex = sections.findIndex((section) => section.id === currentSection?.id);
  const fieldsForSection = textSchema.filter((field) => isInRange(field.order, currentSection?.textRange));
  const checkboxesForSection = checkboxSchema.filter((field) => isInRange(field.order, currentSection?.checkboxRange));
  const groupsForSection = FME1_SECTION_GROUPS[currentSection?.id] || [];
  const goToSectionIndex = (index) => {
    const nextSection = sections[index];
    if (nextSection) {
      setActiveSection(nextSection.id);
      questionnaireNavigate(
        `${questionnaireLocation.pathname}${questionnaireLocation.search}#${nextSection.id}`,
        { state: questionnaireLocation.state }
      );
    }
  };

  const handleSectionChange = (value) => {
    setActiveSection(value);
    questionnaireNavigate(
      `${questionnaireLocation.pathname}${questionnaireLocation.search}#${value}`,
      { state: questionnaireLocation.state }
    );
  };

  const renderPeriodFields = (group) => {
    if (!group.periodFields) {
      return null;
    }

    const renderDatePart = (fieldKey, index) => {
      const placeholders = ["DD", "MM", "YYYY"];
      return (
        <TextField
          key={fieldKey}
          placeholder={placeholders[index]}
          value={data?.[fieldKey] ?? ""}
          onChange={(event) => setField(fieldKey, event.target.value)}
          size="small"
          sx={{ width: { xs: "100%", sm: index === 2 ? 100 : 80 } }}
        />
      );
    };

    return (
      <Box>
        <Typography variant="subtitle1">{group.periodFields.question}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 1 }} alignItems={{ xs: "stretch", sm: "center" }} useFlexGap>
          <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
            <Typography>From:</Typography>
            {group.periodFields.from.map(renderDatePart)}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
            <Typography>To:</Typography>
            {group.periodFields.to.map(renderDatePart)}
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderDateRows = (group) => {
    if (!group.dateRows) {
      return null;
    }

    const placeholders = ["DD", "MM", "YYYY"];
    const renderDatePart = (fieldKey, index) => (
      <TextField
        key={fieldKey}
        placeholder={placeholders[index]}
        value={data?.[fieldKey] ?? ""}
        onChange={(event) => setField(fieldKey, event.target.value)}
        size="small"
        sx={{ width: { xs: "100%", sm: index === 2 ? 100 : 80 } }}
      />
    );

    return (
      <Box>
        <Typography variant="subtitle1">{group.dateRows.question}</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {group.dateRows.rows.map((row) => {
            const naValue = data?.[row.naKey] ?? "";
            return (
              <Stack
                key={`${group.title}-${row.label}`}
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                <Typography sx={{ width: { xs: "100%", sm: 90 } }}>{row.label}</Typography>
                {row.fields.map(renderDatePart)}
                {row.naKey ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isCheckedValue(naValue)}
                        onChange={() => setField(row.naKey, isCheckedValue(naValue) ? "" : "Checked")}
                      />
                    }
                    label="N/A"
                  />
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  };

  const renderFieldRows = (group) => {
    if (!group.fieldRows) {
      return null;
    }

    return (
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ mt: group.question ? 1 : 0 }}
        useFlexGap
      >
        {group.fieldRows.map((row) => (
          <TextField
            key={row.key}
            label={row.label}
            value={data?.[row.key] ?? ""}
            onChange={(event) => setField(row.key, event.target.value)}
            size="small"
            fullWidth
          />
        ))}
      </Stack>
    );
  };

  const renderAnnualServiceChargeLayout = (group) => {
    if (!group.annualServiceChargeLayout) {
      return null;
    }

    const amountQuestion = textSchema.find((field) => field.field_key === "3.1 £")?.question || "3.1 What is the annual Service charge payable by this Property?";
    const detailsQuestion = textSchema.find((field) => field.field_key === "3.1")?.question || "3.1.1 If there is also a 'fixed' Rentcharge, please confirm the amount and explain why";

    return (
      <Box>
        <Typography variant="subtitle1">{amountQuestion}</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Amount including VAT"
            value={value("3.1 £")}
            onChange={(event) => setField("3.1 £", event.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={detailsQuestion}
            value={value("3.1")}
            onChange={(event) => setField("3.1", event.target.value)}
            size="small"
            fullWidth
          />
        </Stack>
      </Box>
    );
  };

  const renderReserveFundLayout = (group) => {
    if (!group.reserveFundLayout) {
      return null;
    }

    const reserveFundQuestion = checkboxSchema.find((field) => field.key === "3.7");
    const reserveSufficientGroup = FME1_COMPOSITE_CHECKBOX_BY_KEY["3.7.2 reserve_fund_sufficient_yes"];
    const reserveFundValue = value("3.7");

    return (
      <Stack spacing={2}>
        {reserveFundQuestion ? (
          <Box>
            <Typography variant="subtitle1">{getCheckboxQuestion(reserveFundQuestion)}</Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
              {getCheckboxOptions(reserveFundQuestion).map((option) => (
                <FormControlLabel
                  key={`reserve-fund-${option}`}
                  control={
                    <Checkbox
                      checked={reserveFundValue === option}
                        onChange={() => setField("3.7", reserveFundValue === option ? "" : option)}
                    />
                  }
                  label={option}
                />
              ))}
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>
              If Yes, confirm the amount collected held in the Reserve Fund
            </Typography>
          </Box>
        ) : null}

        <TextField
          label={textSchema.find((field) => field.field_key === "3.7.1 £")?.question || "3.7.1 Amount collected from Owners of the Property"}
          value={value("3.7.1 £")}
          onChange={(event) => setField("3.7.1 £", event.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label={textSchema.find((field) => field.field_key === "3.7.1 Managed Areas £")?.question || "3.7.1 Amount held for the entirety of the Managed Areas"}
          value={value("3.7.1 Managed Areas £")}
          onChange={(event) => setField("3.7.1 Managed Areas £", event.target.value)}
          size="small"
          fullWidth
        />

        {reserveSufficientGroup ? (
          <Box>
            <Typography variant="subtitle1">{reserveSufficientGroup.question}</Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
              {reserveSufficientGroup.options.map((option) => {
                const currentValue = value(option.key);
                return (
                  <FormControlLabel
                    key={`reserve-sufficient-${option.key}`}
                    control={
                      <Checkbox
                        checked={isCheckedValue(currentValue)}
                        onChange={() => {
                          const willCheck = !isCheckedValue(currentValue);
                          reserveSufficientGroup.keys.forEach((key) => setField(key, ""));
                          if (willCheck) {
                            setField(option.key, "Checked");
                          }
                        }}
                      />
                    }
                    label={option.label}
                  />
                );
              })}
            </Stack>
          </Box>
        ) : null}

        <TextField
          label={textSchema.find((field) => field.field_key === "3.7.3")?.question || "3.7.3 If No, supply details if the Reserve Fund is not expected to be sufficient"}
          value={value("3.7.3")}
          onChange={(event) => setField("3.7.3", event.target.value)}
          size="small"
          fullWidth
        />
      </Stack>
    );
  };

  const toggleNoticeParty = (party, isSelected) => {
    setField(party.selectedKey, isSelected ? "" : "Checked");
    if (isSelected) {
      setField(party.amountKey, "");
      setField(party.emailKey, "");
    }
  };

  const renderNoticeLayout = () => (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1">
          Who accepts service of the Notice of Transfer & Charge?
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {FME1_NOTICE_PARTIES.map((party) => {
            const isSelected = isCheckedValue(value(party.selectedKey));
            return (
              <Stack
                key={party.selectedKey}
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
                useFlexGap
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleNoticeParty(party, isSelected)}
                    />
                  }
                  label={party.label}
                  sx={{ minWidth: { md: 220 } }}
                />
                <TextField
                  label="Amount including VAT"
                  value={value(party.amountKey)}
                  onChange={(event) => setField(party.amountKey, event.target.value)}
                  size="small"
                  fullWidth
                  disabled={!isSelected}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCheckedValue(value(party.emailKey))}
                      onChange={() => setField(party.emailKey, isCheckedValue(value(party.emailKey)) ? "" : "Checked")}
                      disabled={!isSelected}
                    />
                  }
                  label="Notice via email"
                  sx={{ minWidth: { md: 180 } }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Box>

      <Typography variant="subtitle1">If other, provide contact details for service:</Typography>

      <TextField
        label="Name"
        value={value("1.6 Name")}
        onChange={(event) => setField("1.6 Name", event.target.value)}
        size="small"
        fullWidth
      />
      <TextField
        label="Address"
        value={value("1.6 Address")}
        onChange={(event) => setField("1.6 Address", event.target.value)}
        size="small"
        fullWidth
      />
      <TextField
        label="Telephone"
        value={value("1.6 Telephone")}
        onChange={(event) => setField("1.6 Telephone", event.target.value)}
        size="small"
        fullWidth
      />
      <TextField
        label="Email"
        value={value("1.6 Email")}
        onChange={(event) => setField("1.6 Email", event.target.value)}
        size="small"
        fullWidth
      />
      <TextField
        label="Capacity"
        value={value("1.6 Capacity eg Landlords lawyer")}
        onChange={(event) => setField("1.6 Capacity eg Landlords lawyer", event.target.value)}
        size="small"
        fullWidth
      />

      <Box>
        <Typography variant="subtitle1">
          1.5.1 Are details other than the incoming owner's name, address and lender name required?
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
          {["Yes", "No"].map((option) => (
            <FormControlLabel
              key={`notice-details-${option}`}
              control={
                <Checkbox
                  checked={value("1.5.1 requires_additional_details") === option}
                  onChange={() =>
                    setField(
                      "1.5.1 requires_additional_details",
                      value("1.5.1 requires_additional_details") === option ? "" : option
                    )
                  }
                />
              }
              label={option}
            />
          ))}
        </Stack>
      </Box>

      <TextField
        label="1.5.1 If Yes, please detail required additional information"
        value={value("1.5.1")}
        onChange={(event) => setField("1.5.1", event.target.value)}
        size="small"
        fullWidth
      />
    </Stack>
  );

  const renderSignatureLayout = () => (
    <Stack spacing={3}>
      <FormControl fullWidth size="small">
        <InputLabel id="fme1-signature-select-label">Signature</InputLabel>
        <Select
          labelId="fme1-signature-select-label"
          label="Signature"
          value={selectedSignatureId}
          onChange={(event) => handleSignatureSelection(event.target.value)}
        >
          <MenuItem value="">Select signature</MenuItem>
          {signatures.map((signature) => (
            <MenuItem key={signature.id} value={String(signature.id)}>
              {signature.name} - {signature.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          }}
        >
          <Box
            sx={{
              p: 3,
              borderRight: { xs: "none", md: "1px solid" },
              borderBottom: { xs: "1px solid", md: "none" },
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Signature
            </Typography>
            <Box
              sx={{
                minHeight: 96,
                borderBottom: "2px dotted",
                borderColor: "text.primary",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                pb: 0.5,
              }}
            >
              {signaturePreviewImage ? (
                <Box
                  component="img"
                  src={signaturePreviewImage}
                  alt={`${value("Print Name") || selectedSignature?.name || "Selected"} signature`}
                  sx={{ maxHeight: 84, maxWidth: "96%", objectFit: "contain", display: "block" }}
                />
              ) : null}
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            <TextField
              label="Dated"
              type="date"
              value={value("Dated")}
              onChange={(event) => set("Dated", event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              p: 3,
              borderRight: { xs: "none", md: "1px solid" },
              borderBottom: { xs: "1px solid", md: "none" },
              borderColor: "divider",
            }}
          >
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Print Name
                </Typography>
                <Box
                  sx={{
                    minHeight: 34,
                    borderBottom: "2px dotted",
                    borderColor: "text.primary",
                    display: "flex",
                    alignItems: "flex-end",
                    pb: 0.5,
                  }}
                >
                  <Typography>{value("Print Name")}</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Company
                </Typography>
                <Box
                  sx={{
                    minHeight: 34,
                    borderBottom: "2px dotted",
                    borderColor: "text.primary",
                    display: "flex",
                    alignItems: "flex-end",
                    pb: 0.5,
                  }}
                >
                  <Typography>{value("Company")}</Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontStyle: "italic", mb: 2 }}>
              Please tick as applicable below, to confirm the capacity in which the answers are given.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              {SIGNATURE_CAPACITY_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={value("signature_capacity") === option.value}
                      onChange={() =>
                        set(
                          "signature_capacity",
                          value("signature_capacity") === option.value ? "" : option.value
                        )
                      }
                    />
                  }
                  label={option.label}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Stack>
  );

  return (
    <Box maxWidth="900px" mx="auto">
      <Tabs
        value={activeSection}
        onChange={(event, value) => handleSectionChange(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {sections.map((section) => (
          <Tab key={section.id} value={section.id} label={section.tabLabel} />
        ))}
      </Tabs>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {currentSection?.title}
          </Typography>
          {currentSection?.description ? (
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {currentSection.description}
            </Typography>
          ) : null}

          <Stack spacing={4}>
            {fieldsForSection.length > 0 || checkboxesForSection.length > 0 ? (
              (groupsForSection.length ? groupsForSection : [{ title: currentSection?.title }]).map((group, groupIndex) => {
                const groupFields = getGroupFields(group, fieldsForSection);
                const dateRowCheckboxKeys = new Set(group.dateRows?.rows.map((row) => row.naKey).filter(Boolean) || []);
                const groupCheckboxes = getGroupCheckboxes(group, checkboxesForSection).filter(
                  (field) => !dateRowCheckboxKeys.has(field.key)
                );
                if (
                  !groupFields.length &&
                  !groupCheckboxes.length &&
                  !group.noticeLayout &&
                  !group.fieldRows &&
                  !group.annualServiceChargeLayout &&
                  !group.reserveFundLayout
                ) {
                  return null;
                }

                return (
                  <Box key={`${currentSection.id}-group-${group.title}-${groupIndex}`}>
                    {group.title ? <Typography variant="h6">{group.title}</Typography> : null}
                    <Stack spacing={2} mt={group.title ? 1 : 0}>
                      {group.signatureLayout ? renderSignatureLayout() : null}
                      {group.noticeLayout ? renderNoticeLayout() : null}
                      {renderPeriodFields(group)}
                      {renderDateRows(group)}
                      {group.question ? <Typography variant="subtitle1">{group.question}</Typography> : null}
                      {renderFieldRows(group)}
                      {renderAnnualServiceChargeLayout(group)}
                      {renderReserveFundLayout(group)}

                      {groupCheckboxes.length && !group.signatureLayout && !group.noticeLayout && !group.reserveFundLayout ? (
                        <Stack spacing={2}>
                          {groupCheckboxes.map((field) => {
                            const compositeGroup = FME1_COMPOSITE_CHECKBOX_BY_KEY[field.key];
                            if (compositeGroup && compositeGroup.keys[0] !== field.key) {
                              return null;
                            }
                            if (compositeGroup) {
                              return (
                                <Box key={`${currentSection.id}-${group.title}-${compositeGroup.keys.join("-")}`}>
                                  <Typography variant="subtitle1">{compositeGroup.question}</Typography>
                                  <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
                                    {compositeGroup.options.map((option) => {
                                      const currentValue = data?.[option.key] ?? "";
                                      return (
                                        <FormControlLabel
                                          key={`${currentSection.id}-${group.title}-${option.key}`}
                                          control={
                                            <Checkbox
                                              checked={isCheckedValue(currentValue)}
                                              onChange={() => {
                                                const willCheck = !isCheckedValue(currentValue);
                                                compositeGroup.keys.forEach((key) => setField(key, ""));
                                                if (willCheck) {
                                                  setField(option.key, "Checked");
                                                }
                                              }}
                                            />
                                          }
                                          label={option.label}
                                        />
                                      );
                                    })}
                                  </Stack>
                                </Box>
                              );
                            }

                            const options = getCheckboxOptions(field);
                            const currentValue = data?.[field.key] ?? "";
                            if (options.length === 1 && options[0] === "Checked") {
                              return (
                                <FormControlLabel
                                  key={`${currentSection.id}-${group.title}-${field.key}-${field.order}`}
                                  control={
                                    <Checkbox
                                      checked={isCheckedValue(currentValue)}
                                      onChange={() => setField(field.key, isCheckedValue(currentValue) ? "" : "Checked")}
                                    />
                                  }
                                  label={getCheckboxQuestion(field)}
                                />
                              );
                            }

                            return (
                              <Box key={`${currentSection.id}-${group.title}-${field.key}-${field.order}`}>
                                <Typography variant="subtitle1">{getCheckboxQuestion(field)}</Typography>
                                <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
                                  {options.map((option) => (
                                    <FormControlLabel
                                      key={`${field.key}-${field.order}-${option}`}
                                      control={
                                        <Checkbox
                                          checked={currentValue === option}
                                          onChange={() => setField(field.key, currentValue === option ? "" : option)}
                                        />
                                      }
                                      label={option}
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : null}

                      {groupFields.length && !group.periodFields && !group.dateRows && !group.fieldRows && !group.annualServiceChargeLayout && !group.reserveFundLayout && !group.signatureLayout && !group.noticeLayout ? (
                        <Stack spacing={2}>
                          {groupFields.map((field) => {
                            const isDate = field.field_type === "DateField";
                            const label = getConfiguredFieldLabel(field, group);
                            return (
                              <TextField
                                key={`${currentSection.id}-${group.title}-${field.field_key}-${field.order}`}
                                label={label}
                                value={data?.[field.field_key] ?? ""}
                                onChange={(event) => setField(field.field_key, event.target.value)}
                                type={isDate ? "date" : "text"}
                                InputLabelProps={isDate ? { shrink: true } : undefined}
                                size="small"
                                fullWidth
                              />
                            );
                          })}
                        </Stack>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          disabled={currentSectionIndex <= 0}
          onClick={() => goToSectionIndex(currentSectionIndex - 1)}
        >
          Previous Page
        </Button>

        <Typography color="text.secondary">
          Section {currentSectionIndex + 1} of {TOTAL_FME1_SECTIONS}
        </Typography>

        <Button
          variant="contained"
          disabled={currentSectionIndex >= sections.length - 1}
          onClick={() => goToSectionIndex(currentSectionIndex + 1)}
        >
          Next Page
        </Button>
      </Box>
    </Box>
  );
}

export default function FME1Form() {
  const { fme1Id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useBackNavigation("/dashboard");
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const lastPathRef = useRef(`${location.pathname}${location.search}${location.hash}`);

  const isNewForm = location.pathname === "/fme1/new" || fme1Id === "new" || !fme1Id;
  const unitId = useMemo(() => {
    if (!isNewForm) return null;
    return new URLSearchParams(location.search).get("unit_id");
  }, [isNewForm, location.search]);

  const [form, setForm] = useState({});
  const [originalForm, setOriginalForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [hasBeenSaved, setHasBeenSaved] = useState(false);
  const [currentFme1Id, setCurrentFme1Id] = useState(isNewForm ? null : fme1Id);
  const [fme1UnitId, setFme1UnitId] = useState(null);
  const [rowVersion, setRowVersion] = useState(1);
  const [fme1Meta, setFme1Meta] = useState({
    status: "Requested",
    requestedDate: "",
    dueByDate: "",
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const isNew = location.pathname === "/fme1/new" || fme1Id === "new" || !fme1Id;
        if (isNew) {
          const unitIdParam = new URLSearchParams(location.search).get("unit_id");
          const today = getToday();
          setFme1Meta({
            status: "Requested",
            requestedDate: today,
            dueByDate: addDays(today, 60),
          });

          if (unitIdParam) {
            setFme1UnitId(parseInt(unitIdParam, 10));
            try {
              const unitData = await getUnitDetails(unitIdParam);
              const initialData = await getInitialFME1Data(parseInt(unitIdParam, 10));
              setForm(initialData);
              setOriginalForm(JSON.parse(JSON.stringify(initialData)));
              if (unitData?.unit?.property_id) {
                const propertyData = await getPropertyById(unitData.unit.property_id);
                setCustomPath([
                  { label: "Properties", path: "/properties" },
                  {
                    label: propertyData?.name || `Property ${unitData.unit.property_id}`,
                    path: `/properties/${unitData.unit.property_id}/details`,
                  },
                  {
                    label: unitData.unit.unit_name || unitData.unit.unit_code || `Unit ${unitData.unit.id}`,
                    path: `/units/${unitData.unit.id}/details`,
                  },
                  { label: "FME1", path: null },
                ]);
              }
            } catch (loadError) {
              console.error("Failed to load initial FME1 data:", loadError);
              setForm({});
              setOriginalForm({});
              setCustomPath([
                { label: "Properties", path: "/properties" },
                { label: "New FME1", path: null },
              ]);
            }
          } else {
            setForm({});
            setOriginalForm({});
          }

          setHasBeenSaved(false);
        } else {
          const result = await getFME1ById(fme1Id);
          const formData = result.formData || {};
          setForm(formData);
          setOriginalForm(JSON.parse(JSON.stringify(formData)));
          setFme1Meta({
            status: result.status || "Requested",
            requestedDate: toDateInput(result.requestedDate),
            dueByDate: toDateInput(result.dueByDate),
          });
          setRowVersion(result.rowVersion || 1);
          setHasBeenSaved(true);
          if (result.unitId) {
            setFme1UnitId(result.unitId);
          }
          if (result.propertyId && result.propertyName) {
            const breadcrumbPath = [
              { label: "Properties", path: "/properties" },
              { label: result.propertyName, path: `/properties/${result.propertyId}/details` },
            ];
            if (result.unitId) {
              breadcrumbPath.push({
                label: result.unitName || result.unitCode || `Unit ${result.unitId}`,
                path: `/units/${result.unitId}/details`,
              });
            }
            breadcrumbPath.push({ label: "FME1", path: null });
            setCustomPath(breadcrumbPath);
          }
        }
      } catch (loadError) {
        console.error(loadError);
        setError("Failed to load FME1 form.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => clearCustomPath();
  }, [clearCustomPath, fme1Id, location.pathname, location.search, setCustomPath]);

  const updateField = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const updateMeta = (patch) => {
    setFme1Meta((previous) => ({ ...previous, ...patch }));
  };

  const persistFme1Meta = async (actualFme1Id, metaRowVersion = rowVersion) => {
    if (!actualFme1Id) return;
    const payload = {};
    if (fme1Meta.status) payload.status = fme1Meta.status;
    if (fme1Meta.dueByDate) payload.due_by_date = fme1Meta.dueByDate;
    if (!Object.keys(payload).length) return;

    try {
      // Use version from preceding save when provided — setState is async and
      // would otherwise send a stale row_version and get concurrency_conflict.
      const response = await updateFME1Meta(actualFme1Id, payload, metaRowVersion);
      if (response.data?.row_version) {
        setRowVersion(response.data.row_version);
      }
      setFme1Meta((previous) => ({
        ...previous,
        status: response.data?.status ?? previous.status,
        requestedDate: toDateInput(response.data?.requested_date) || previous.requestedDate,
        dueByDate: toDateInput(response.data?.due_by_date) || previous.dueByDate,
      }));
    } catch {
      showToast("Failed to update FME1 status", "error");
    }
  };

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(originalForm),
    [form, originalForm]
  );

  useEffect(() => {
    lastPathRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const onClick = (event) => {
      if (!isDirty || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
      const anchor = event.target.closest("a");
      if (!anchor || anchor.getAttribute("target") === "_blank") return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      let url;
      try {
        url = new URL(anchor.href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      event.preventDefault();
      setPendingNavigation({ type: "path", path: `${url.pathname}${url.search}${url.hash}` });
      setConfirmDialogOpen(true);
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isDirty]);

  useEffect(() => {
    const onPopState = () => {
      if (!isDirty) return;
      const targetPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const currentPath = lastPathRef.current;
      if (targetPath === currentPath) return;
      setPendingNavigation({ type: "path", path: targetPath });
      setConfirmDialogOpen(true);
      navigate(currentPath, { replace: true });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDirty, navigate]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const attemptSaveWithCheck = async (after) => {
    if (after === "export") {
      setExporting(true);
    }

    try {
      setSaving(true);
      let actualFme1Id = currentFme1Id;
      let savedForm = form;
      let latestRowVersion = rowVersion;

      if (isNewForm && !actualFme1Id) {
        const unitIdToUse = unitId || fme1UnitId;
        if (!unitIdToUse) {
          showToast("Unit ID is required", "error");
          return;
        }
        const createResponse = await createFME1(unitIdToUse);
        actualFme1Id = createResponse.data?.fme1_id;
        if (!actualFme1Id) {
          throw new Error("Failed to create FME1");
        }

        setFme1UnitId(parseInt(unitIdToUse, 10));
        setCurrentFme1Id(actualFme1Id);
        const result = await getFME1ById(actualFme1Id);
        const initialFormData = result.formData || {};
        const mergedForm = { ...initialFormData, ...form };
        savedForm = mergedForm;
        setForm(mergedForm);
        latestRowVersion = result.rowVersion || 1;
        setRowVersion(latestRowVersion);
        const saveResponse = await saveFME1(actualFme1Id, mergedForm, latestRowVersion);
        if (saveResponse.data?.row_version) {
          latestRowVersion = saveResponse.data.row_version;
          setRowVersion(latestRowVersion);
        }
        navigate(`/fme1/${actualFme1Id}`, { replace: true, state: { from: location.state?.from } });
      } else {
        const saveResponse = await saveFME1(actualFme1Id, form, rowVersion);
        if (saveResponse.data?.row_version) {
          latestRowVersion = saveResponse.data.row_version;
          setRowVersion(latestRowVersion);
        }
      }

      setHasBeenSaved(true);
      setOriginalForm(JSON.parse(JSON.stringify(savedForm)));
      await persistFme1Meta(actualFme1Id, latestRowVersion);

      if (after === "export") {
        await downloadEditableFme1Pdf(actualFme1Id);
        showToast("PDF exported");
      } else {
        showToast("FME1 saved");
      }
    } catch (saveError) {
      console.error(saveError);
      showToast(getApiErrorMessage(saveError, "Operation failed"), "error");
    } finally {
      setSaving(false);
      setExporting(false);
    }
  };

  const navigateBack = () => {
    const fromPath = location.state?.from;
    if (fromPath && fromPath.startsWith("/properties/") && fromPath.endsWith("/details") && fme1UnitId) {
      navigate(`/units/${fme1UnitId}/details`);
      return;
    }
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    goBack();
  };

  const handleConfirmNavigation = async () => {
    setConfirmDialogOpen(false);
    if (!hasBeenSaved && currentFme1Id) {
      try {
        await deleteFME1(currentFme1Id);
      } catch (deleteError) {
        console.error("Failed to delete unsaved FME1", deleteError);
      }
    }

    if (pendingNavigation?.type === "path" && pendingNavigation.path) {
      navigate(pendingNavigation.path);
    } else {
      navigateBack();
    }
    setPendingNavigation(null);
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box maxWidth="1100px" mx="auto" px={2} pb={6}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography variant="h4">FME1 Questionnaire</Typography>
            <Typography color="text.secondary">
              {isNewForm ? "New FME1 Form" : `FME1 ID: ${fme1Id}`}
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <FME1MetaPanel meta={fme1Meta} onChange={updateMeta} />

            <Divider />

            <FME1Questionnaire data={form} setField={updateField} />

            <Divider />

            <Stack direction="row" justifyContent="space-between">
              <Button
                variant="outlined"
                onClick={() => {
                  if (isDirty) {
                    setPendingNavigation({ type: "back" });
                    setConfirmDialogOpen(true);
                    return;
                  }
                  navigateBack();
                }}
              >
                {hasBeenSaved ? "Return" : "Cancel"}
              </Button>

              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={() => attemptSaveWithCheck("save")} disabled={saving}>
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => attemptSaveWithCheck("export")}
                  disabled={saving}
                  startIcon={exporting ? <CircularProgress size={18} /> : null}
                >
                  {exporting ? "Exporting..." : "Export PDF"}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      <Dialog open={confirmDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>{hasBeenSaved ? "Unsaved Changes" : "Discard Changes?"}</DialogTitle>
        <DialogContent>
          <Typography>
            {hasBeenSaved
              ? "You have unsaved changes. Are you sure you want to leave? Any unsaved data will be lost."
              : "You have unsaved changes. If you cancel, this FME1 will be deleted. Are you sure you want to continue?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Stay</Button>
          <Button variant="contained" color={hasBeenSaved ? "primary" : "error"} onClick={handleConfirmNavigation}>
            {hasBeenSaved ? "Leave" : "Cancel & Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((current) => ({ ...current, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
